import { useContext } from 'react'

import {
  SanityProductBundleSlotProduct,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { hasObject } from '@lib/helpers'
import { getOptionLabel } from '@lib/product'
import { ProductVariantSelectionContext } from '@lib/product-variant-selection-context'

import ProductOptionValue from '@blocks/product/product-option-value'
import { getButtonStyles } from '@components/buttons/button'
import RadioGroup from '@components/radio-group'

interface BundleSlotOptionsProps {
  product: SanityProductBundleSlotProduct
  variant: SanityProductVariantFragment | null
  availableOptions: Record<string, string[]>
  optionNames: string[]
  onChange(variant: SanityProductVariantFragment): void
}

const BundleSlotOptions = ({
  product,
  variant,
  optionNames,
  availableOptions,
  onChange,
}: BundleSlotOptionsProps) => {
  const { selectedOptions } = useContext(ProductVariantSelectionContext)

  return (
    <div>
      {product.options.map((option, index) => {
        const filteredValues = [
          ...option.values.filter((value) =>
            availableOptions[option.name]?.some(
              (optionValue) => optionValue === value,
            ),
          ),
        ]

        if (
          filteredValues.length === 0 ||
          !variant ||
          !optionNames.includes(option.name)
        ) {
          return null
        }

        const filteredOption = {
          ...option,
          values: filteredValues,
        }

        // Handle changes in option value - get new selected product variant
        const handleChange = (newValue: string) => {
          if (!variant) {
            return
          }

          const newOptions = variant.options.map((variantOption) =>
            variantOption.name === option.name
              ? {
                  ...variantOption,
                  value: newValue,
                }
              : variantOption,
          )

          // Find variant that matches all new options
          const newVariant = product.variants?.find(({ options }) =>
            options.every((variantOption) =>
              hasObject(newOptions, variantOption),
            ),
          )

          if (newVariant) {
            onChange(newVariant)
          }
        }

        // Get selected value for current option
        const value =
          variant.options.find(
            (variantOption) => variantOption.name === option.name,
          )?.value ?? ''

        return (
          <div className="flex mt-2 py-2 items-center">
            <div className="mr-12 text-base font-semibold uppercase tracking-wider">
              {getOptionLabel(product.optionNames ?? [], filteredOption) ||
                filteredOption.name}
            </div>

            <RadioGroup
              value={value}
              onChange={handleChange}
              className="flex flex-wrap -mt-1 ml-auto justify-end"
            >
              {filteredOption.values.map((value) => (
                <ProductOptionValue
                  key={value}
                  value={value}
                  product={product}
                  option={filteredOption}
                  optionSettings={product.optionSettings ?? []}
                  strictMatch
                  className="mt-1 mb-1 ml-2"
                  labelClassName={getButtonStyles({
                    variant: 'secondary',
                    color: 'black',
                    size: 'small',
                    isActive: !!selectedOptions?.some(
                      (selectedOption) =>
                        selectedOption.name === filteredOption.name &&
                        selectedOption.value === value,
                    ),
                  })}
                />
              ))}
            </RadioGroup>
          </div>
        )
      })}
    </div>
  )
}

export default BundleSlotOptions
